import { isPlatformServer } from '@angular/common';
import { Injector, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { RESPONSE } from 'src/express.tokens';
import { routes } from 'utils/routes';

const normalizePath = (u: string) => u.split(/[?#]/)[0].replace(/\/+$/, '');

export const landingPageGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const injector = inject(Injector);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  const response = isPlatformServer(platformId) ? inject(RESPONSE) : null;

  const slug = route.params['slug'];
  if (!slug) return router.parseUrl('/not-found');

  const lang = (route.params['lang'] ?? 'it') as 'it' | 'en' | 'es' | 'fr' | 'de';
  const discoverRoute = routes[lang]?.['routes.discover'];
  if (!discoverRoute) return router.parseUrl('/not-found');

  return from(import('../pages/landing-page/landing-page.query.generated')).pipe(
    switchMap(({ CustomPageBySlugGQL }) =>
      injector
        .get(CustomPageBySlugGQL)
        .fetch({ slug, locale: lang }, { errorPolicy: 'ignore', fetchPolicy: 'network-only' }),
    ),
    map(r => {
      const page = r.data?.customPageBySlug;
      if (!page?.id) return router.parseUrl('/not-found');

      const correctSlug = page.localizedSlug || slug;
      const targetUrl = `/${lang}/${discoverRoute}/${correctSlug}`;

      const currentUrl = normalizePath(state.url);
      const target = normalizePath(targetUrl);

      if (currentUrl === target) return true;

      if (isPlatformServer(platformId) && response) {
        response.redirect(301, targetUrl);
        response.end();
        return false;
      }

      return router.parseUrl(targetUrl);
    }),
    catchError(() => of(router.parseUrl('/not-found'))),
  );
};
